import React, { HTMLAttributes, useMemo, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { SessionsTabProps } from '../types';

export const SessionsTab: React.FC<SessionsTabProps> = ({
  sessions,
  sessionStats,
  isLoadingSessions,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  // Get unique status values and count sessions for each status
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    
    sessions.forEach(session => {
      const status = session.status || 'unknown';
      counts[status] = (counts[status] || 0) + 1;
    });
    
    return counts;
  }, [sessions]);
  
  // Get status keys sorted by count (descending)
  const statusKeys = useMemo(() => {
    return Object.keys(statusCounts).sort((a, b) => statusCounts[b] - statusCounts[a]);
  }, [statusCounts]);
  
  // Filter sessions based on search term
  const filteredSessions = useMemo(() => {
    if (!searchTerm.trim()) return sessions;
    
    const term = searchTerm.toLowerCase().trim();
    return sessions.filter(session => 
      session._id?.toLowerCase().includes(term) ||
      session.user?.email?.toLowerCase().includes(term) ||
      session.podId?.toLowerCase().includes(term) ||
      (session.status?.toLowerCase().includes(term))
    );
  }, [sessions, searchTerm]);

  // Session status badges with appropriate colors
  const getStatusBadge = (session: any) => {
    const status = session.status || 'unknown';
    
    // Color mapping based on status
    let bgColor = 'bg-gray-100';
    
    switch (status.toLowerCase()) {
      case 'active':
      case 'online':
      case 'connected':
        bgColor = 'bg-green-100';
        break;
      case 'idle':
      case 'waiting':
        bgColor = 'bg-blue-100';
        break;
      case 'offline':
      case 'disconnected':
        bgColor = 'bg-gray-100';
        break;
      case 'error':
      case 'failed':
        bgColor = 'bg-red-100';
        break;
      case 'pending':
      case 'connecting':
        bgColor = 'bg-yellow-100';
        break;
      default:
        // If pod exists check
        if (!session.podId) {
          bgColor = 'bg-gray-100';
        } else {
          // Check if pod exists (this should be passed in or derived)
          const existingPodIds = new Set(sessions
            .filter(s => s.podExists)
            .map(s => s.podId));
          
          bgColor = existingPodIds.has(session.podId) ? 'bg-green-100' : 'bg-red-100';
        }
    }
    
    return <Badge variant="outline" className={bgColor}>{status}</Badge>;
  };

  // Render status cards dynamically based on distinct status values
  const renderStatusCards = () => {
    // First ensure we show the total card
    const cards = [
      <Card key="total">
        <CardHeader className="pb-2">
          <CardTitle className="text-2xl font-bold">{sessions.length}</CardTitle>
          <CardDescription>Total Sessions</CardDescription>
        </CardHeader>
      </Card>
    ];
    
    // Add cards for all status values
    statusKeys.forEach(status => {
      cards.push(
        <Card key={status}>
          <CardHeader className="pb-2">
            <CardTitle className="text-2xl font-bold">{statusCounts[status]}</CardTitle>
            <CardDescription>{status}</CardDescription>
          </CardHeader>
          <CardContent>
            {getStatusBadge({ status })}
          </CardContent>
        </Card>
      );
    });
    
    return cards;
  };

  // Determine the appropriate grid column count based on the number of status cards
  const gridColsClass = useMemo(() => {
    const totalCards = 1 + statusKeys.length; // Total + all status cards
    
    if (totalCards <= 4) return 'grid-cols-4';
    if (totalCards <= 5) return 'grid-cols-5';
    if (totalCards <= 6) return 'grid-cols-6';
    return 'grid-cols-4 md:grid-cols-6 lg:grid-cols-8'; // Responsive grid for many statuses
  }, [statusKeys.length]);

  return (
    <div className="space-y-6">
      {/* Sessions Status Cards */}
      <div className={`grid ${gridColsClass} gap-4`}>
        {renderStatusCards()}
      </div>
      
      {/* Search Input */}
      <div className="flex items-center space-x-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
          <Input
            placeholder="Search sessions..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Badge variant="outline">
          {filteredSessions.length} {filteredSessions.length === 1 ? 'result' : 'results'}
        </Badge>
      </div>
      
      {/* Sessions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Sessions</CardTitle>
          <CardDescription>
            All WhatsApp sessions running in the cluster
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingSessions ? (
            <div className="h-96 flex items-center justify-center">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className="h-96 flex items-center justify-center text-slate-500">
              No sessions found
            </div>
          ) : (
            <div className="border rounded-md overflow-hidden">
              {/* Table Header */}
              <div className="grid grid-cols-5 gap-4 p-3 bg-slate-100/50 font-medium text-sm">
                <div>Session ID</div>
                <div>User</div>
                <div>Pod</div>
                <div>Status</div>
                <div>Last Active</div>
              </div>
              
              {/* Virtual Table */}
              <div className="h-96">
                <Virtuoso
                  data={filteredSessions}
                  totalCount={filteredSessions.length}
                  itemContent={(index, session) => (
                    <div key={session._id} className="grid grid-cols-5 gap-4 p-3 border-t text-sm">
                      <div className="truncate">{session._id || 'N/A'}</div>
                      <div className="truncate">
                        {session.user?.email || 'N/A'}
                      </div>
                      <div className="truncate">{session.podId || 'None'}</div>
                      <div>{getStatusBadge(session)}</div>
                      <div className="truncate">
                        {session.lastActive ? new Date(session.lastActive).toLocaleString() : 'N/A'}
                      </div>
                    </div>
                  )}
                  components={{
                    Scroller: React.forwardRef((props: HTMLAttributes<HTMLDivElement>, ref) => (
                      <div {...props} ref={ref} style={{ ...props.style, overflowX: 'hidden' }} />
                    ))
                  }}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SessionsTab; 